import { captureException } from '@sentry/nextjs'
import { Map } from 'immutable'
import { debounce, isString, throttle } from 'lodash-es'
import get from 'lodash-es/get'
import { finalize } from 'rxjs'
import { webSocket } from 'rxjs/webSocket'
import { z } from 'zod'
import { createWithEqualityFn as create } from 'zustand/traditional'

import {
  AssetTrackingData,
  AssetTrackingDataSchema,
  sockets,
  type SocketStatus,
  socketStatus,
  statusMapping,
  statusResolver,
  TelemetryStatus,
  TelemetryStore,
  WebSocketMessage,
  WebSocketType,
} from '../types/telemetry.types'
import { getTelemetrySocketUrl } from '../utils'
import { deriveIsMoving } from './telemetry.utils'

/** This is an optimization to batch the updates to the store */
const collector = {
  items: [] as [AssetTrackingData['sourceId'], AssetTrackingData][],
  processor: undefined as
    | ((assets: { items: [AssetTrackingData['sourceId'], AssetTrackingData][] }) => void)
    | undefined,
  enqueue(item: [AssetTrackingData['sourceId'], AssetTrackingData]) {
    this.items.push(item)

    this.processor?.(this)
  },
}

const messageSchema = z.string().transform((content, ctx) => {
  if (content.startsWith(':')) {
    const [status, msg] = content.slice(1).split(' ', 2)
    if (!(status in statusMapping)) {
      captureException(new Error(`(Websocket) Unknown status: ${status}`))
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'unknown status',
      })
      return z.never
    }

    return socketStatus.parse(get(statusMapping, status, msg))
  }
  try {
    content = JSON.parse(content)
  } catch (error) {
    captureException(error)
    console.log('Parsing problem', error, content)
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'invalid json',
    })
    return z.never
  }
  try {
    return AssetTrackingDataSchema.parse(content)
  } catch (error) {
    captureException(error)
    console.log('Parsing problem', error, content)
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'invalid object',
    })
    if (error instanceof z.ZodError) {
      error.issues.forEach((issue) => {
        ctx.addIssue(issue)
      })
    }
    return z.never
  }
})
// .pipe(parseSchema)

const createSocket = (fleet: string, _accessToken: string): WebSocketType =>
  webSocket<WebSocketMessage>({
    url: getTelemetrySocketUrl(fleet),
    deserializer: (e) => messageSchema.parse(e.data) as AssetTrackingData | SocketStatus,
    serializer: (e) => `${e}`,
  })

export const useTelemetryStore = create<TelemetryStore>((setState, getState) => ({
  status: TelemetryStatus.UNINITIALIZED,
  tracking: Map(),
  socketStates: {},
  activeListeners: 0,
  incrementActiveListeners() {
    setState((state) => ({ activeListeners: state.activeListeners + 1 }))
  },
  decrementActiveListeners() {
    setState((state) => ({ activeListeners: state.activeListeners - 1 }))
  },
  closeSocket(fleet: string) {
    const state = getState()
    if (fleet in state.socketStates) {
      setState((s) => ({
        socketStates: { ...s.socketStates, [fleet]: socketStatus.enum.CLOSED },
      }))

      try {
        sockets[fleet]?.complete?.()
      } catch (error) {
        console.error('Error closing socket', error)
        captureException(error)
      }
    }
  },
  ensureSocket(fleet, accessToken) {
    const state = getState()
    if (!sockets[fleet] || state.socketStates[fleet] === socketStatus.enum.CLOSED) {
      sockets[fleet] = createSocket(fleet, accessToken)
      sockets[fleet].next(accessToken)
      setState((s) => ({
        status: TelemetryStatus.CONNECTING,
        socketStates: { ...s.socketStates, [fleet]: socketStatus.enum.DISCONNECTED },
      }))
    } else {
      setState({ status: TelemetryStatus.CONNECTING })
    }

    return sockets[fleet]
  },
  startListeningToSocket: debounce(
    (fleet: string, accessToken?: string): (() => void) => {
      const state = getState()
      const socketState = state.socketStates[fleet]
      let socket = sockets[fleet]

      collector.processor ||= throttle(
        (bg) => {
          setState((s) => ({
            tracking: s.tracking.merge(bg.items),
          }))
          bg.items = []
        },
        2_000,
        {
          // updates arrive in bursts, instead of firing when the first item is
          // received and then delaying until the time-out has expired
          // before processing the rest, we don't fire on the leading
          // edge and instead wait until the end of the time-out to
          // process what has been received thus far.
          // if we don't do this, the first time the map loads only a single
          // vehicle gets its position shown, and after the
          // time-out the rest get updated
          leading: false,
          trailing: true,
        },
      )

      if (
        !socket ||
        ([socketStatus.enum.DISCONNECTED, socketStatus.enum.CLOSED] as SocketStatus[]).includes(
          socketState,
        )
      ) {
        if (!accessToken) {
          throw new Error('Access token is required to create a new socket')
        } else {
          socket = state.ensureSocket(fleet, accessToken)
        }
      }

      const subscription = socket
        .pipe(
          finalize(() => {
            delete sockets[fleet]
            setState((s) => ({
              socketStates: { ...s.socketStates, [fleet]: socketStatus.enum.DISCONNECTED },
            }))
          }),
        )
        .subscribe({
          next: (data: Omit<WebSocketMessage, string>) => {
            if (socketStatus.safeParse(data).success) {
              const newSocketState = data as SocketStatus
              setState((s) => ({
                status: statusResolver[newSocketState],
                socketStates: { ...s.socketStates, [fleet]: newSocketState },
              }))
            } else if (!isString(data)) {
              const asset = data as AssetTrackingData
              const trackedAsset: AssetTrackingData = {
                ...asset,
                isMoving: deriveIsMoving(asset),
              }
              collector.enqueue([trackedAsset.sourceId, trackedAsset])
            } else {
              console.error('Unknown message', data)
            }
          },
          error: (error: unknown) => {
            if (error instanceof CloseEvent) {
              delete sockets[fleet]
              setState((s) => ({
                socketStates: { ...s.socketStates, [fleet]: socketStatus.enum.DISCONNECTED },
              }))
            } else if (error instanceof Event && error.type === 'error') {
              // WebSocket error events don't contain useful information - just log without Sentry
              console.warn('WebSocket connection error', { fleet })
              delete sockets[fleet]
              setState((s) => ({
                socketStates: { ...s.socketStates, [fleet]: socketStatus.enum.DISCONNECTED },
              }))
            } else {
              console.error('error', error)
              captureException(error)
              delete sockets[fleet]
              setState((s) => ({
                status: TelemetryStatus.ERROR,
                socketStates: { ...s.socketStates, [fleet]: socketStatus.enum.DISCONNECTED },
              }))
            }
            getState().startListeningToSocket(fleet, accessToken)
          },
          complete: () => {
            const currentState = getState()
            const currentSocketState = currentState.socketStates[fleet]

            delete sockets[fleet]
            setState((s) => ({
              status: TelemetryStatus.UNINITIALIZED,
              socketStates: { ...s.socketStates, [fleet]: socketStatus.enum.CLOSED },
            }))

            if (
              ([socketStatus.enum.TIMEOUT, socketStatus.enum.TRACKING] as SocketStatus[]).includes(
                currentSocketState,
              )
            ) {
              currentState.startListeningToSocket(fleet, accessToken)
            }
          },
        })

      return () => {
        subscription.unsubscribe()
      }
    },
    100,
    { leading: true, trailing: true },
  ),
  socket(fleet: string | undefined): WebSocketType | undefined {
    return fleet == null ? undefined : sockets[fleet]
  },
}))
